import { useEffect, useMemo, useState } from "react";
import { ShippingService } from "../services/shipping.service";

const FALLBACK_CURRENCY = "USD";
const FALLBACK_LOCALE = "en-US";

const SHOP_LOCALE =
  typeof navigator !== "undefined" && navigator.language
    ? navigator.language
    : FALLBACK_LOCALE;

export interface ShopCurrencyContext {
  /** Código ISO 4217 de la moneda de la tienda (ej. "USD"). */
  currency: string;
  /**
   * Locale habitual del navegador (`navigator.language`), para {@link Intl}
   * y agrupación/decimales coherentes con el comerciante.
   */
  locale: string;
  /**
   * Formatea un importe con la moneda de la tienda y el locale del navegador
   * (p. ej. "$1,000.00" vs "$1.000,00"). Los decimales dependen de la moneda
   * (JPY sin decimales, USD con 2, etc.).
   */
  formatPrice: (amount: number | string) => string;
}

export function useShopCurrency(): ShopCurrencyContext {
  const [currency, setCurrency] = useState<string>(FALLBACK_CURRENCY);

  useEffect(() => {
    let cancelled = false;
    ShippingService.getLogistics()
      .then((data) => {
        if (!cancelled) setCurrency(data.currency);
      })
      .catch((err) => {
        // Fallo silencioso: mantener moneda por defecto sin bloquear la vista.
        console.error("[useShopCurrency] Failed to fetch currency:", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const formatPrice = useMemo(() => {
    const toNumber = (amount: number | string) => {
      const n = typeof amount === "number" ? amount : Number(amount);
      return Number.isFinite(n) ? n : 0;
    };
    try {
      const formatter = new Intl.NumberFormat(SHOP_LOCALE, {
        style: "currency",
        currency,
      });
      return (amount: number | string) => formatter.format(toNumber(amount));
    } catch {
      // Código de moneda inválido (raro si viene del enum de Shopify).
      // Mostrar decimales + código para no romper la UI.
      const formatter = new Intl.NumberFormat(SHOP_LOCALE, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return (amount: number | string) =>
        `${currency} ${formatter.format(toNumber(amount))}`;
    }
  }, [currency]);

  return { currency, locale: SHOP_LOCALE, formatPrice };
}
